'use client'

import { type RefObject, useEffect } from 'react'

const TYPE_MS = 45
const DELETE_MS = 25
const HOLD_MS = 1800
const GAP_MS = 400
/** Reduced motion: no typing, just swap whole phrases at a readable pace. */
const REDUCED_SWAP_MS = 3500

/**
 * Cycles `phrases` through a field's placeholder with a typing effect: type one out, hold it,
 * erase it, move to the next. Writes the `placeholder` attribute directly rather than through
 * state, so the owning component doesn't re-render every keystroke of the animation.
 *
 * While `active` is false (the field has text, or something else replaces the placeholder) the
 * loop stops and `idle` is shown instead; it restarts from the first phrase when re-activated.
 * Honours prefers-reduced-motion by swapping whole phrases.
 *
 * A phrase wider than the field's free first line (after padding and any `text-indent`) is
 * skipped, so the placeholder never wraps — a content-sized field would otherwise grow a line.
 * If none fit, `idle` stays up.
 */
export function useTypewriterPlaceholder(
  ref: RefObject<HTMLTextAreaElement | HTMLInputElement | null>,
  phrases: string[],
  { active, idle }: { active: boolean; idle: string }
) {
  // Restart only when the phrases themselves change (e.g. the focused account's name).
  const phrasesKey = phrases.join('\u0000')

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (!active || phrases.length === 0) {
      el.placeholder = idle
      return
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const measure = document.createElement('canvas').getContext('2d')
    // Re-read on every phrase: the field's width and indent change as the widget resizes.
    const fits = (phrase: string) => {
      if (!measure) return true
      const style = getComputedStyle(el)
      const free =
        el.clientWidth -
        parseFloat(style.paddingLeft) -
        parseFloat(style.paddingRight) -
        (parseFloat(style.textIndent) || 0)
      // An unlaid-out field (0 width) can't say yet; let the phrase through.
      if (free <= 0) return true
      measure.font = style.font
      return measure.measureText(phrase).width <= free
    }
    // From `from`, the first phrase that fits on one line, or -1 when none does.
    const nextFitting = (from: number) => {
      for (let step = 0; step < phrases.length; step++) {
        const candidate = (from + step) % phrases.length
        if (fits(phrases[candidate])) return candidate
      }
      return -1
    }
    let index = 0
    let length = 0
    let deleting = false
    let timer: ReturnType<typeof setTimeout>

    const tick = () => {
      if (!deleting && length === 0) {
        const fitting = nextFitting(index)
        if (fitting < 0) {
          el.placeholder = idle
          timer = setTimeout(tick, REDUCED_SWAP_MS)
          return
        }
        index = fitting
      }
      // Array.from: split by code point, so an emoji or astral character isn't cut in half.
      const glyphs = Array.from(phrases[index])
      if (reduceMotion) {
        el.placeholder = phrases[index]
        index = (index + 1) % phrases.length
        timer = setTimeout(tick, REDUCED_SWAP_MS)
        return
      }
      if (!deleting) {
        length += 1
        el.placeholder = glyphs.slice(0, length).join('')
        if (length >= glyphs.length) {
          deleting = true
          timer = setTimeout(tick, HOLD_MS)
          return
        }
        timer = setTimeout(tick, TYPE_MS)
        return
      }
      length -= 1
      el.placeholder = glyphs.slice(0, length).join('')
      if (length <= 0) {
        deleting = false
        index = (index + 1) % phrases.length
        timer = setTimeout(tick, GAP_MS)
        return
      }
      timer = setTimeout(tick, DELETE_MS)
    }

    el.placeholder = ''
    timer = setTimeout(tick, GAP_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- phrasesKey stands in for phrases
  }, [ref, phrasesKey, active, idle])
}
